import { T } from "./Translate";
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  DialogContentText,
  Button,
  List,
  ListItem,
  Box,
  CardMedia,
  Grid,
  Skeleton,
  Switch,
  FormControlLabel,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import MediaQuery from "react-responsive";
import { ArrowBackIos as ArrowBackIosIcon } from "../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../icons";
import { Search as SearchIcon } from "../icons";
import { Edit as EditIcon } from "../icons";
import { DeleteOutline as DeleteIcon } from "../icons";
import BottomToolbar from "./BottomToolbar";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { VideoLibrary as VideoLibraryIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLoggedIn } from "../session";
import { auth, db, getToken } from "../firebase";
import Atras from "./BackButton";
import EditSectionTabs from "./EditSectionTabs";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import CopyToClipboardComponent from "./CopyToClipboardButton";
import CouponExpiryFields, { couponExpiryToDoc } from "../components/CouponExpiryFields";
import { OFFER_CATEGORIES } from "../offerCategories";
import { getOfferPricing } from "./homeUtils";
import DiscountPriceBreakdown from "../components/DiscountPriceBreakdown";
import OfferPaymentBadges from "../components/OfferPaymentBadges";
import PaymentInfoFields from "../components/PaymentInfoFields";
import { brandColors, barColors } from "../theme";
import { pageContentSx } from "../pageLayout";
import { uploadButtonSx, uploadProgressSx, mediaPreviewStyle, mediaPreviewBoxSx, removeMediaButtonSx, removeMediaIconSx } from "./mediaFormStyles";

import SearchClearAdornment from "../components/SearchClearAdornment";
const pageHeaderInnerSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  maxWidth: { xs: "100%", md: "980px" },
  mx: "auto",
  px: 2,
  boxSizing: "border-box",
};

const normalizeMediaArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

function EditOffers() {
  const [aspectRatio] = useState("16:9");

  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(160);

  // Mide el alto real del AppBar fijo en vez de adivinar un valor en px (ver
  // mismo criterio en home.js/events.js).
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) return;
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleLoadedMetadata = () => {};

  const [open, setOpen] = useState(false);
  // Cuando está activo, borra el campo "country" al guardar para que
  // filterOffersByCountry (homeUtils.js) muestre la oferta en cualquier país.
  const [worldwide, setWorldwide] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);

  const [loading, setLoading] = useState(true);

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  const [product, setProduct] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [offer, setOffer] = useState("");
  const [price, setPrice] = useState("");
  const [priceError] = useState("");
  const [url, setUrl] = useState("");
  const [cupon, setCupon] = useState("");
  // Cobro con PayPal en Notiline (ver publishOffer.js / OfferCheckout.js).
  const [sellWithPaypal, setSellWithPaypal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("");
  const [couponExpiry, setCouponExpiry] = useState({ expiresAt: "", maxUses: "" });
  const [paymentInfo, setPaymentInfo] = useState({
    installments: "",
    installmentsNoInterest: false,
    promo: "",
    acceptedCards: [],
  });

  const handleEditClick = (offerId) => {
    const selected = offers.find((o) => o.id === offerId);
    if (!selected) return;
    setSelectedOfferId(offerId);
    setProduct(selected.product || "");
    setDescription(selected.description || "");
    setCategory(selected.category || "");
    setOffer(selected.offer || "");
    setUrl(selected.url || "");
    setCupon(selected.cupon || "");
    setSellWithPaypal(!!selected.paypalEmail);
    setPaypalEmail(selected.paypalEmail || "");
    setCouponExpiry({
      expiresAt: selected.cuponExpiresAt || "",
      maxUses: selected.cuponMaxUses ? String(selected.cuponMaxUses) : "",
    });
    setPrice(selected.price != null ? String(selected.price) : "");
    setImages(normalizeMediaArray(selected.images || selected.imageUrls || selected.image));
    setVideos(normalizeMediaArray(selected.videos));
    setPaymentInfo({
      installments: selected.installments || "",
      installmentsNoInterest: !!selected.installmentsNoInterest,
      promo: selected.promo || "",
      acceptedCards: selected.acceptedCards || [],
    });
    // Sin country guardado = visible en todos los países (ver filterOffersByCountry en homeUtils.js)
    setWorldwide(!selected.country);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleSubmit = async () => {
    // El precio es opcional (cupón general de la tienda, ver publishOffer.js).
    // Con PayPal el enlace y el cupón son opcionales (el cupón ni se muestra),
    // pero hacen falta precio y correo de PayPal.
    if (!product || !description || !offer || (!sellWithPaypal && (!url || !cupon))) {
      alert("Por favor, completa todos los campos obligatorios.");
      return;
    }
    const cleanPaypalEmail = paypalEmail.trim();
    if (sellWithPaypal && (!(Number(price) > 0) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanPaypalEmail))) {
      alert("Para cobrar con PayPal completá un precio y un correo de PayPal válido.");
      return;
    }

    try {
      const offerRef = doc(db, "offers", selectedOfferId);
      await updateDoc(offerRef, {
        product,
        description,
        ...(category ? { category } : {}),
        offer,
        price: price ? Number(price) : "",
        url,
        cupon: sellWithPaypal ? "" : cupon,
        ...couponExpiryToDoc(sellWithPaypal ? { expiresAt: "", maxUses: "" } : couponExpiry),
        paypalEmail: sellWithPaypal ? cleanPaypalEmail : null,
        images,
        videos,
        installments: paymentInfo.installments,
        installmentsNoInterest: paymentInfo.installmentsNoInterest,
        promo: paymentInfo.promo,
        acceptedCards: paymentInfo.acceptedCards,
        ...(worldwide ? { country: deleteField() } : {}),
      });
      handleClose();
    } catch (error) {
      console.error("Error al actualizar la oferta:", error);
    }
  };

  const handleImageUpload = () => {};
  const handleVideoUpload = () => {};
  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };
  const handleRemoveVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };
  const handlePriceChange = (e) => setPrice(e.target.value);
  const handleDelete = async (offerId) => {
    try {
      const offerRef = doc(db, "offers", offerId);
      await deleteDoc(offerRef);
    } catch (error) {
      console.error("Error al eliminar la oferta:", error);
    }
  };

  const [offers, setOffers] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [resultado, setResultados] = useState([]);
  // Muestra de a 10 publicaciones y suma otras 10 al hacer scroll; vuelve a
  // la primera tanda cuando cambia la búsqueda.
  const { limit: listLimit, sentinelRef: listSentinelRef } = useInfiniteScroll(busqueda, 10);

  const buscar = useCallback(() => {
    const valorBusqueda = busqueda.toLowerCase();
    const resultadosFiltrados = offers.filter((offer) =>
      offer.product.toLowerCase().includes(valorBusqueda)
    );
    setResultados(resultadosFiltrados);
  }, [busqueda, offers]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const formatDate = (date) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    } else if (date && date.toDate instanceof Function) {
      return date.toDate().toLocaleDateString();
    }
    return "";
  };

  const navigate = useNavigate();

  // ?edit=<id> abre directo el editor de esa publicación (ej. desde el menú ⋮
  // del perfil). Se saca el parámetro para que no se vuelva a abrir.
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam || !offers.some((item) => item.id === editParam)) return;
    handleEditClick(editParam);
    setSearchParams({}, { replace: true });
  }, [editParam, offers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkAuthAndFetchOffers = async () => {
      if (!isLoggedIn()) {
        navigate("/login");
        return;
      }

      try {
        const token = await getToken();

        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }
        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "offers"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const offersData = snapshot.docs.map((doc) => {
            const data = doc.data();
            if (typeof data.product !== "string") {
              console.error(
                "Unexpected data type for name field:",
                data.product
              );
              return { ...data, product: "", image: "", imageUrls: [] };
            }
            let imageUrls = [];
            if (data.images && Array.isArray(data.images)) {
              imageUrls = data.images;
            } else if (data.images && typeof data.images === "string") {
              imageUrls = [data.images];
            }

            return {
              ...data,
              id: doc.id,
              createdAt: data.createdAt ? data.createdAt.toDate() : null,
              imageUrls,
            };
          });
          setOffers(offersData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener ofertas:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchOffers();
  }, [navigate]);

  const getDomainFromUrl = (url) => {
    if (!url) {
      return url;
    }

    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "http://" + url;
    }
    const domain = new URL(url).hostname;

    const dotIndex = domain.lastIndexOf(".");

    if (dotIndex === -1) {
      return domain;
    }

    const shortenedDomain = domain.substring(0, dotIndex);

    if (shortenedDomain.length > 15) {
      return shortenedDomain.slice(0, 15) + "...";
    }

    return shortenedDomain;
  };

  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  const handleOpenImageModal = (images) => {
    setModalImages(images);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === modalImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? modalImages.length - 1 : prevIndex - 1
    );
  };

  const styles = {
    cont: {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      margin: 0,
      padding: 0,
      boxSizing: "border-box",
    },
    img: {
      maxWidth: "150px",
      maxHeight: "40px",
      display: "block",
      margin: "20px auto",
    },
    slogan: {
      color: "white",
      fontSize: "14px",
      fontWeight: "bold",
      paddingBottom: "-5px",
    },
    iconContainer: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid rgba(255,255,255,0.2)",
      marginTop: "20px",
      justifyContent: "space-around",
    },
    iconContainerL: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid rgba(255,255,255,0.2)",
      justifyContent: "space-around",
    },
    backSlogan: {
      display: "block",
      width: "100vw",
      height: "19px",
      backgroundColor: "#030553",
      textAlign: "center",
    },
    listItem: {
      display: "block",
      width: "100%",
      border: "none",
      margin: "16px auto",
      padding: "clamp(14px, 4vw, 20px)",
      boxSizing: "border-box",
      // Links largos sin espacios en la descripción ensanchaban la tarjeta
      // más allá de la pantalla en celular.
      overflowWrap: "anywhere",
      backgroundColor: brandColors.backgroundPaper,
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      },
    },
    typography: {
      marginBottom: "15px",
    },
    textField: {
      marginBottom: "30px",
    },
    contList: {
      marginTop: "-30px",
    },
    inputS: {
      display: "block",
      width: "90vw",
      height: "30px",
      marginRight: "30px",
      padding: "3px 0px 3px 20px",
      fontSize: "14px",
      border: "1px solid grey",
      borderRadius: "30px",
    },
    name: {
      color: brandColors.accent,
      fontSize: "clamp(20px, 5.5vw, 28px)",
      fontWeight: "bold",
      marginBottom: "0px",
    },
    title: {
      fontSize: "20px",
      marginBottom: "12px",
      color: brandColors.textPrimary,
      fontWeight: "500",
      lineHeight: "1.4",
    },
    description: {
      marginBottom: "16px",
      fontWeight: "400",
      color: brandColors.textSecondary,
      lineHeight: "1.6",
      fontSize: "15px",
    },
    offer: {
      fontSize: "17px",
      marginBottom: "16px",
      backgroundColor: "#1f2a63",
      color: "#c7d2ff",
      padding: "12px 16px",
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: "500",
    },
    cupon: {
      backgroundColor: "#1a1a2e",
      padding: "16px",
      borderRadius: "8px",
      fontSize: "18px",
      marginBottom: "16px",
      textAlign: "center",
      color: "#a0b4ff",
      fontWeight: "500",
      border: "1px dashed rgba(255,255,255,0.2)",
    },
    created: {
      fontSize: "10px",
      marginBottom: "15px",
      marginTop: "15px",
    },
    user: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      width: "100%",
      height: "100%",
      marginTop: "10px",
      color: "white",
    },
    contUser: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      marginBottom: "15px",
    },
    search: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100vw",
      height: "35px",
      border: "1.5px solid rgba(255,255,255,0.2)",
      backgroundColor: "#1a1a2e",
    },
    imageGrid: {
      marginTop: "15px",
      marginBottom: "15px",
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    noImage: {
      width: "100%",
      height: "100px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: brandColors.backgroundDefault,
      color: brandColors.textSecondary,
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "200px",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    additionalImagesOverlay: {
      position: "absolute",
      bottom: "10px",
      right: "10px",
      width: "60px",
      height: "60px",
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      color: "white",
      cursor: "pointer",
    },
    additionalImagesCount: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#ffffff",
    },

    imageModal: {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      position: "fixed",
      top: 0,
      left: 0,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      zIndex: 1000,
    },
    imageModalContent: {
      maxWidth: "90%",
      maxHeight: "90%",
      position: "relative",
      backgroundColor: "#1e1e1e",
      padding: "20px",
      borderRadius: "10px",
      boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.5)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    imageModalImage: {
      width: "100%",
      height: "auto",
      borderRadius: "10px",
      marginBottom: "20px",
    },
    imageModalNavigation: {
      position: "absolute",
      bottom: "10px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      justifyContent: "space-between",
      width: "auto",
      padding: "10px",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: "5px",
    },
    imageModalDescription: {
      textAlign: "center",
      marginBottom: "20px",
    },
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar ref={appBarRef} position="fixed" color="primary">
        <Toolbar disableGutters sx={{ padding: "10px 0" }}>
          <Box sx={pageHeaderInnerSx}>
            <Atras color="inherit" />
            <Typography variant="h5">
              <T>Publicaciones</T>
            </Typography>
          </Box>
        </Toolbar>
        <EditSectionTabs />
        <Box sx={{ borderTop: `1px solid ${barColors.border}`, py: 1 }}>
          <Box sx={pageHeaderInnerSx}>
            <TextField
              id="search-offers-input"
              fullWidth
              size="small"
              placeholder="Buscar nombre de producto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoComplete="off"
              InputProps={{
                endAdornment: <SearchClearAdornment value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />,
                startAdornment: <SearchIcon sx={{ mr: 1, color: barColors.textMuted }} />,
                sx: {
                  color: barColors.text,
                  backgroundColor: barColors.activeBg,
                  borderRadius: "999px",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Box>
      </AppBar>
      <Container disableGutters sx={{ mt: `${appBarHeight}px`, mb: 10 }}>
        <div>
          {loading ? (
            <List>
              {[1, 2, 3].map((i) => (
                <ListItem key={i} style={{ display: "block", padding: "20px", marginBottom: "16px", backgroundColor: brandColors.backgroundPaper, borderRadius: "8px" }}>
                  <Box style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width="30%" height={24} />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Skeleton variant="rectangular" width="30%" height={150} sx={{ borderRadius: "8px" }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: "6px", mb: 1 }} />
                      <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: "6px" }} />
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <>
              {busqueda.trim() !== "" ? (
                resultado.length > 0 ? (
                <List sx={{ pb: 4 }}>
                  {resultado.slice(0, listLimit).map((selectedOffer) => (
                    <ListItem key={selectedOffer.id} style={styles.listItem}>
                          <Box style={styles.contUser}>
                            <CardMedia
                              component="img"
                              image={selectedOffer.image}
                              title="Perfil"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                marginRight: 16,
                                border: `2.5px solid ${brandColors.mode === 'light' ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.3)'}`,
                              }}
                            />
                            <Typography style={styles.name}>
                              {selectedOffer.name}
                            </Typography>
                          </Box>
                          <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: { xs: "column", md: "row" },
                          justifyContent: "flex-start",
                          alignItems: "center",
                          width: "100%",
                          mt: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            flexDirection: "column",
                            width: { xs: "100%", md: "20%" },
                            alignItems: { xs: "center", md: "flex-start" },
                            mb: { xs: 3, md: 0 },
                          }}
                        >
                          {selectedOffer.videos &&
                          selectedOffer.videos.length > 0 ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom:
                                  aspectRatio === "16:9" ? "56.25%" : "177.78%",
                                backgroundColor: "#000",
                                overflow: "hidden",
                                borderRadius: "5px",
                              }}
                            >
                              <video
                                src={selectedOffer.videos[0]}
                                controls
                                muted
                                playsInline
                                autoPlay
                                onLoadedMetadata={handleLoadedMetadata}
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : selectedOffer.images &&
                            selectedOffer.images.length > 0 ? (
                            <Box style={styles.imageContainer}>
                              <CardMedia
                                component="img"
                                image={selectedOffer.images[0]}
                                alt={`Imagen principal de ${selectedOffer.name}`}
                                style={styles.mainImage}
                              />
                              {selectedOffer.images.length > 1 && (
                                <Box
                                  style={styles.additionalImagesOverlay}
                                  onClick={() =>
                                    handleOpenImageModal(selectedOffer.images)
                                  }
                                >
                                  <Typography
                                    style={styles.additionalImagesCount}
                                  >
                                    +{selectedOffer.images.length - 1}
                                  </Typography>
                                  <Typography sx={{ color: "#ffffff" }}><T>Ver más</T></Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box style={styles.noImage}>
                              <Typography><T>No hay medios disponibles</T></Typography>
                            </Box>
                          )}
                        </Box>

                        <Box
                          sx={{
                            width: { xs: "100%", md: "70%" },
                            pl: { xs: 0, md: 3 },
                          }}
                        >
                          <Typography style={styles.title}>
                            <T>Titulo del producto:</T> {selectedOffer.product}
                          </Typography>
                          <Typography style={styles.description}>
                            <T>Descripción:</T> {selectedOffer.description}
                          </Typography>

                          <Box sx={{ mb: 2 }}>
                            {(() => {
                              const pricing = getOfferPricing(selectedOffer);
                              // Sin precio = cupón general de la tienda: no hay precio que mostrar.
                              if (!pricing && !selectedOffer.price) return null;
                              return pricing ? (
                                <DiscountPriceBreakdown
                                  originalPrice={pricing.previousPrice}
                                  discountPercent={selectedOffer.offer}
                                  finalPrice={pricing.finalPrice}
                                />
                              ) : (
                                <Typography style={styles.offer}>
                                  <T>Precio del producto:</T> ${selectedOffer.price}
                                </Typography>
                              );
                            })()}
                          </Box>
                          <OfferPaymentBadges offer={selectedOffer} />
                          <CopyToClipboardComponent
                            textToCopy={selectedOffer.cupon}
                            offer={selectedOffer}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            href={`http://${selectedOffer.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ width: "100%", mt: 1, mb: 1 }}
                          >
                            <T>Comprar en</T> {getDomainFromUrl(selectedOffer.url)}
                          </Button>
                          <Typography style={styles.created}>
                            <T>Fecha de publicación:</T>{" "}
                            {formatDate(selectedOffer.createdAt)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(selectedOffer.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Editar</T>
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              fullWidth
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(selectedOffer.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Eliminar</T>
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <Typography variant="h6"><T>No encontramos resultados</T></Typography>
                  <Typography variant="body2"><T>No hay publicaciones que coincidan con tu búsqueda.</T></Typography>
                </Box>
              )
              ) : (
                <List sx={{ pb: 4 }}>
                  {offers.slice(0, listLimit).map((offers) => (
                    <ListItem key={offers.id} style={styles.listItem}>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          flexDirection: { xs: "column", md: "row" },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: "100%", md: "30%" },
                            mr: { xs: 0, md: 2 },
                            mb: { xs: 2, md: 0 },
                          }}
                        >
                          <Box style={styles.contUser}>
                            <CardMedia
                              component="img"
                              image={offers.image}
                              title="Perfil"
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                marginRight: 16,
                                border: `2.5px solid ${brandColors.mode === 'light' ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.3)'}`,
                              }}
                            />
                            <Typography style={styles.name}>
                              {offers.name}
                            </Typography>
                          </Box>
                          {offers.videos && offers.videos.length > 0 ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom:
                                  aspectRatio === "16:9" ? "56.25%" : "177.78%",
                                backgroundColor: "#000",
                                overflow: "hidden",
                                borderRadius: "5px",
                              }}
                            >
                              <video
                                src={offers.videos[0]}
                                controls
                                muted
                                playsInline
                                autoPlay
                                onLoadedMetadata={handleLoadedMetadata}
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : offers.images && offers.images.length > 0 ? (
                            <Box style={styles.imageContainer}>
                              <CardMedia
                                component="img"
                                image={offers.images[0]}
                                alt={`Imagen principal de ${offers.name}`}
                                style={styles.mainImage}
                              />
                              {offers.images.length > 1 && (
                                <Box
                                  style={styles.additionalImagesOverlay}
                                  onClick={() =>
                                    handleOpenImageModal(offers.images)
                                  }
                                >
                                  <Typography
                                    style={styles.additionalImagesCount}
                                  >
                                    +{offers.images.length - 1}
                                  </Typography>
                                  <Typography sx={{ color: "#ffffff" }}><T>Ver más</T></Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box style={styles.noImage}>
                              <Typography><T>No hay medios disponibles</T></Typography>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ width: { xs: "100%", md: "70%" } }}>
                          <Typography style={styles.title}>
                            {offers.product}
                          </Typography>
                          <Typography style={styles.description}>
                            {offers.description}
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            href={`http://${offers.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ width: "100%", mt: 1, mb: 1 }}
                          >
                            <T>Comprar en</T> {getDomainFromUrl(offers.url)}
                          </Button>
                          <Box sx={{ mb: 2 }}>
                            {(() => {
                              const pricing = getOfferPricing(offers);
                              // Sin precio = cupón general de la tienda: no hay precio que mostrar.
                              if (!pricing && !offers.price) return null;
                              return pricing ? (
                                <DiscountPriceBreakdown
                                  originalPrice={pricing.previousPrice}
                                  discountPercent={offers.offer}
                                  finalPrice={pricing.finalPrice}
                                />
                              ) : (
                                <Typography style={styles.offer}>
                                  <T>Precio del Producto:</T> {offers.price}
                                </Typography>
                              );
                            })()}
                          </Box>
                          <OfferPaymentBadges offer={offers} />
                          <CopyToClipboardComponent textToCopy={offers.cupon} offer={offers} />
                          <Typography style={styles.created}>
                            <T>Fecha de publicación:</T> {formatDate(offers.createdAt)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(offers.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Editar</T>
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              fullWidth
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(offers.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Eliminar</T>
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
              {(busqueda.trim() !== "" ? resultado : offers).length > listLimit && (
                <Box ref={listSentinelRef} sx={{ height: 1 }} />
              )}
            </>
          )}
        </div>
      </Container>
      <Dialog open={open} onClose={handleClose} sx={{ zIndex: 20000 }}>
        <DialogTitle><T>Editar Descuento</T></DialogTitle>
        <DialogContent>
          <MediaQuery minWidth={1224}>
            <TextField
              required
              autoFocus
              margin="dense"
              id="product"
              label={<T>Nombre del producto</T>}
              type="text"
              fullWidth
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            />
            <TextField
              required
              margin="dense"
              id="description"
              label={<T>Descripción del producto</T>}
              type="text"
              multiline
              rows={2}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              select
              margin="dense"
              id="category"
              label={<T>Categoría</T>}
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {OFFER_CATEGORIES.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <T>{c.label}</T>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              margin="dense"
              id="offer"
              label={<T>Descuento</T>}
              type="text"
              fullWidth
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
            />
            <TextField
              margin="dense"
              id="price"
              label={sellWithPaypal ? <T>Precio en USD</T> : <T>Precio en USD (opcional)</T>}
              required={sellWithPaypal}
              type="number"
              fullWidth
              value={price}
              onChange={handlePriceChange}
              error={!!priceError}
              helperText={priceError}
            />
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={<Switch checked={sellWithPaypal} onChange={(e) => setSellWithPaypal(e.target.checked)} />}
              label={<T>Cobrar con PayPal en Notiline</T>}
            />
            {sellWithPaypal && (
              <TextField
                required
                margin="dense"
                label={<T>Tu correo de PayPal</T>}
                type="email"
                fullWidth
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                helperText={<T>Los compradores pagan directo a este correo y acuerdan el envío con vos por chat.</T>}
              />
            )}
            <TextField
              required={!sellWithPaypal}
              margin="dense"
              id="url"
              label={sellWithPaypal ? <T>URL (opcional)</T> : <T>URL</T>}
              type="text"
              fullWidth
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {!sellWithPaypal && (
              <>
                <TextField
                  required
                  margin="dense"
                  id="cupon"
                  label={<T>Cupón</T>}
                  type="text"
                  fullWidth
                  value={cupon}
                  onChange={(e) => setCupon(e.target.value)}
                />
                <CouponExpiryFields value={couponExpiry} onChange={setCouponExpiry} />
              </>
            )}

            <PaymentInfoFields value={paymentInfo} onChange={setPaymentInfo} />

            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={worldwide}
                  onChange={(e) => setWorldwide(e.target.checked)}
                />
              }
              label={<T>Visible en todos los países</T>}
            />

            <label
              htmlFor="upload-image-button"
              style={{ width: "100%", marginTop: "15px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <AddPhotoAlternateIcon />
                <T>Subir Imágenes</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="upload-image-button"
              multiple
            />

            <label
              htmlFor="upload-video-button"
              style={{ width: "100%", marginTop: "10px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <VideoLibraryIcon />
                <T>Subir Videos</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: "none" }}
              id="upload-video-button"
              multiple
            />

            <Grid container sx={{ mt: 2 }}>
              {images.map((imageUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={mediaPreviewBoxSx(false)}>
                    <img
                      src={imageUrl}
                      alt={`Imagen ${index + 1}`}
                      style={mediaPreviewStyle}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveImage(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
              {videos.map((videoUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                    <video
                      src={videoUrl}
                      controls
                      style={{ width: "100%", borderRadius: "10px" }}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveVideo(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </MediaQuery>
          <MediaQuery maxWidth={1223}>
            <TextField
              required
              autoFocus
              margin="dense"
              id="product"
              label={<T>Nombre del producto</T>}
              type="text"
              fullWidth
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            />
            <TextField
              required
              margin="dense"
              id="description"
              label={<T>Descripción del producto</T>}
              type="text"
              multiline
              rows={2}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              select
              margin="dense"
              id="category"
              label={<T>Categoría</T>}
              fullWidth
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {OFFER_CATEGORIES.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <T>{c.label}</T>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              margin="dense"
              id="offer"
              label={<T>Descuento</T>}
              type="text"
              fullWidth
              value={offer}
              onChange={(e) => setOffer(e.target.value)}
            />
            <TextField
              margin="dense"
              id="price"
              label={sellWithPaypal ? <T>Precio en USD</T> : <T>Precio en USD (opcional)</T>}
              required={sellWithPaypal}
              type="number"
              fullWidth
              value={price}
              onChange={handlePriceChange}
              error={!!priceError}
              helperText={priceError}
            />
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={<Switch checked={sellWithPaypal} onChange={(e) => setSellWithPaypal(e.target.checked)} />}
              label={<T>Cobrar con PayPal en Notiline</T>}
            />
            {sellWithPaypal && (
              <TextField
                required
                margin="dense"
                label={<T>Tu correo de PayPal</T>}
                type="email"
                fullWidth
                value={paypalEmail}
                onChange={(e) => setPaypalEmail(e.target.value)}
                helperText={<T>Los compradores pagan directo a este correo y acuerdan el envío con vos por chat.</T>}
              />
            )}
            <TextField
              required={!sellWithPaypal}
              margin="dense"
              id="url"
              label={sellWithPaypal ? <T>URL (opcional)</T> : <T>URL</T>}
              type="text"
              fullWidth
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            {!sellWithPaypal && (
              <>
                <TextField
                  required
                  margin="dense"
                  id="cupon"
                  label={<T>Cupón</T>}
                  type="text"
                  fullWidth
                  value={cupon}
                  onChange={(e) => setCupon(e.target.value)}
                />
                <CouponExpiryFields value={couponExpiry} onChange={setCouponExpiry} />
              </>
            )}

            <PaymentInfoFields value={paymentInfo} onChange={setPaymentInfo} />

            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={worldwide}
                  onChange={(e) => setWorldwide(e.target.checked)}
                />
              }
              label={<T>Visible en todos los países</T>}
            />

            <label
              htmlFor="upload-image-button"
              style={{ width: "100%", marginTop: "15px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <AddPhotoAlternateIcon />
                <T>Subir Imágenes</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="upload-image-button"
              multiple
            />

            <label
              htmlFor="upload-video-button"
              style={{ width: "100%", marginTop: "10px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <VideoLibraryIcon />
                <T>Subir Videos</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: "none" }}
              id="upload-video-button"
              multiple
            />

            <Grid container sx={{ mt: 2 }}>
              {images.map((imageUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={mediaPreviewBoxSx(false)}>
                    <img
                      src={imageUrl}
                      alt={`Imagen ${index + 1}`}
                      style={mediaPreviewStyle}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveImage(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
              {videos.map((videoUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                    <video
                      src={videoUrl}
                      controls
                      style={{ width: "100%", borderRadius: "10px" }}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveVideo(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </MediaQuery>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <T>Cancelar</T>
          </Button>
          <Button onClick={handleSubmit} color="primary">
            <T>Guardar</T>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-labelledby="image-modal-title"
        aria-describedby="image-modal-description"
        fullScreen
      >
        <DialogTitle id="image-modal-title"><T>Imágenes del producto</T></DialogTitle>
        <DialogContent>
          <DialogContentText id="image-modal-description">
            {openImageModal && (
              <Box style={styles.imageModalContent}>
                {modalImages && modalImages.length > 0 ? (
                  <CardMedia
                    component="img"
                    image={modalImages[currentImageIndex]}
                    alt={`Imagen ${currentImageIndex + 1} del alojamiento`}
                    style={styles.imageModalImage}
                  />
                ) : (
                  <Typography><T>No hay imágenes disponibles</T></Typography>
                )}
                <Box style={styles.imageModalNavigation}>
                  <IconButton onClick={handlePreviousImage}>
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton onClick={handleNextImage}>
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageModal}><T>Cerrar</T></Button>
        </DialogActions>
      </Dialog>
      <BottomToolbar />
    </Container>
  );
};

export default EditOffers;
